import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { GET_MESSAGE_THREADS } from '../graphql/queries/messageThreads';

const AllMessageThreads: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [authLoading, user, navigate]);

  const { data, loading, error } = useQuery(GET_MESSAGE_THREADS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const threads = data?.currentUser?.messageThreads?.items || [];
  const totalCount = data?.currentUser?.messageThreads?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard?tab=messages')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Tilbage til beskeder
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Alle beskedtråde</h1>
          <p className="mt-2 text-gray-600">Oversigt over alle dine samtaler</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Indlæser...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">Der opstod en fejl ved indlæsning af beskedtråde</p>
            </div>
          ) : threads.length > 0 ? (
            <>
              <div>
                {threads.map((thread: any, index: number) => {
                  const otherParticipant = thread.participants?.find((participant: any) => participant.id !== user?.id) || thread.latestMessageSentBy;
                  const isOtherParticipantOnline = !!otherParticipant?.isOnline;
                  const senderAvatarSrc = otherParticipant?.avatarImageUrl || otherParticipant?.avatar;

                  return (
                    <Link
                      key={thread.id}
                      to={`/conversation/${thread.id}`}
                      className={`block p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200 ${
                        index !== threads.length - 1 ? 'border-b border-gray-200' : ''
                      } ${thread.unreadMessageCount > 0 ? 'bg-green-50' : ''}`}
                    >
                      <div className="flex items-start">
                        <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium mr-4 overflow-hidden">
                          {senderAvatarSrc ? (
                            <img
                              src={senderAvatarSrc}
                              alt={otherParticipant?.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span>{otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="text-sm font-medium text-gray-900">{thread.subject}</h3>
                            <p className="text-xs text-gray-500">{formatDate(thread.latestMessage?.sentAt)}</p>
                          </div>
                          <p className="text-xs text-gray-500 mb-1">{otherParticipant?.name}</p>
                          <p className="text-sm text-gray-600 line-clamp-2">{thread.latestMessage?.content}</p>
                        </div>
                        <div className="ml-4 flex flex-row items-center gap-2">
                          {isOtherParticipantOnline && (
                            <span className="inline-block h-3 w-3 rounded-full bg-green-600"></span>
                          )}
                          {thread.unreadMessageCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {thread.unreadMessageCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Viser {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} af {totalCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Side {currentPage} af {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <MessageSquare className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen beskeder</h3>
              <p className="mt-1 text-sm text-gray-500">Du har endnu ingen beskedtråde.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllMessageThreads;
